import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
axios.defaults.baseURL = process.env.REACT_APP_API_URL || "http://localhost:5000";
axios.defaults.withCredentials = true;
axios.interceptors.request.use((config) => {
  // Don't clobber a per-request Authorization header (e.g. the admin face-challenge token).
  if (config.headers && config.headers["Authorization"]) return config;
  const token = localStorage.getItem("auth_token");
  if (token) config.headers["Authorization"] = `Bearer ${token}`;
  return config;
});
const AppContext = createContext(null);
const FORCE_LIGHT_THEME_ONLY = false;
const AppProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState(() => "light");
  const [language, setLanguage] = useState(() => {
    const stored = localStorage.getItem("taweelai_lang");
    if (stored) return stored;
    return "ar";
  });
  useEffect(() => {
    const effective = FORCE_LIGHT_THEME_ONLY ? "light" : theme;
    document.documentElement.setAttribute("data-theme", effective);
    document.documentElement.dir = language === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = language;
    document.documentElement.style.colorScheme = effective === "light" ? "light" : "dark";
    const tc = document.querySelector('meta[name="theme-color"]');
    if (tc) tc.setAttribute("content", effective === "light" ? "#ffffff" : "#0a1f0d");
  }, [theme, language]);
  const checkAuth = useCallback(async () => {
    try {
      const res = await axios.get("/api/auth/me");
      setUser(res.data.user);
      if (!FORCE_LIGHT_THEME_ONLY) {
        const storedTheme = localStorage.getItem("taweelai_theme");
        const hasStoredTheme = storedTheme === "dark" || storedTheme === "light";
        if (!hasStoredTheme) {
          const serverTheme = res.data.user.preferences?.theme;
          const nextTheme = serverTheme === "dark" || serverTheme === "light" ? serverTheme : "light";
          applyTheme(nextTheme, false);
        }
      } else {
        applyTheme("light", false);
      }
      if (res.data.user.preferences?.language) {
        applyLanguage(res.data.user.preferences.language, false);
      } else {
        applyLanguage("ar", false);
      }
    } catch {
      setUser(null);
      localStorage.removeItem("taweelai_theme");
      applyTheme("light", false);
    } finally {
      setLoading(false);
    }
    // applyTheme/applyLanguage are stable helpers declared below. Listing them
    // here would change checkAuth's identity on every render and re-fire the
    // auth effect in a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);
  const applyTheme = (t, save = true) => {
    const next = FORCE_LIGHT_THEME_ONLY ? "light" : t === "dark" || t === "light" ? t : "light";
    setTheme(next);
    localStorage.setItem("taweelai_theme", next);
    if (save && user) axios.patch("/api/auth/preferences", { theme: next }).catch(() => {
    });
  };
  const applyLanguage = (lang, save = true) => {
    const newLang = typeof lang === "string" && (lang === "en" || lang === "ar") ? lang : language === "en" ? "ar" : "en";
    setLanguage(newLang);
    i18n.changeLanguage(newLang);
    localStorage.setItem("taweelai_lang", newLang);
    document.documentElement.dir = newLang === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = newLang;
    if (save && user) axios.patch("/api/auth/preferences", { language: newLang }).catch(() => {
    });
  };
  const toggleTheme = () => {
    if (!FORCE_LIGHT_THEME_ONLY) applyTheme(theme === "dark" ? "light" : "dark");
  };
  const toggleLanguage = (val) => applyLanguage(val);
  const login = (userData) => setUser(userData);
  const logout = async () => {
    try {
      await axios.post("/api/auth/signout");
    } catch {
    }
    localStorage.removeItem("auth_token");
    localStorage.removeItem("taweelai_theme");
    setUser(null);
    applyTheme("light", false);
  };
  const updateMessageCount = (count) => setUser((prev) => prev ? { ...prev, messageCount: count } : prev);
  const updatePlan = (plan, remainingSeconds) => setUser((prev) => prev ? { ...prev, plan, remainingSeconds } : prev);
  return <AppContext.Provider value={{
    user,
    setUser,
    loading,
    theme,
    language,
    toggleTheme,
    toggleLanguage,
    login,
    logout,
    updateMessageCount,
    updatePlan,
    checkAuth
  }}>
      {children}
    </AppContext.Provider>;
};
const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used within AppProvider");
  return ctx;
};
export {
  AppProvider,
  useApp
};
